import React from 'react';
import { X, CheckCircle, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

type ToastType = 'success' | 'error';

interface ToastProps {
  message: string;
  isVisible: boolean;
  onClose: () => void;
  type?: ToastType;
}

const Toast: React.FC<ToastProps> = ({
  message,
  isVisible,
  onClose,
  type = 'error'
}) => {
  const Icon = type === 'success' ? CheckCircle : AlertCircle;
  
  // Dynamic styles based on type
  const getTypeStyles = () => {
    if (type === 'success') {
      return {
        border: 'lp-border-emerald-500/20',
        iconColor: 'lp-text-emerald-500',
        glowColor: '16, 185, 129'  // RGB for emerald-500
      };
    }
    return {
      border: 'lp-border-red-500/20',
      iconColor: 'lp-text-red-500',
      glowColor: '239, 68, 68'  // RGB for red-500
    };
  };

  const styles = getTypeStyles();

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          className="lp-fixed lp-top-4 lp-left-1/2 lp-transform -lp-translate-x-1/2 lp-z-50 lp-w-full lp-max-w-md"
        >
          <div className="lp-relative">
            {/* Content */}
            <div className={`lp-relative lp-z-20 lp-bg-black/95 ${styles.border} lp-border lp-rounded-xl lp-backdrop-blur-sm lp-shadow-xl`}>
              <div className="lp-flex lp-items-center lp-p-4 lp-gap-3">
                <Icon className={`lp-w-5 lp-h-5 ${styles.iconColor} lp-flex-shrink-0`} />
                <p className="lp-text-gray-300 lp-font-medium">
                  {message}
                </p>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onClose}
                  className="lp-ml-auto -lp-mr-1 lp-p-1 lp-rounded-full hover:lp-bg-white/5 lp-transition-colors"
                >
                  <X className="lp-w-4 lp-h-4 lp-text-gray-400" />
                </motion.button>
              </div>
            </div>
            
            {/* Background glow effect below the content */}
            <div 
              className="lp-absolute lp-z-10 lp-w-[140%] lp-h-[140%] lp-left-1/2 lp-top-1/2 -lp-translate-x-1/2 -lp-translate-y-1/2"
              style={{
                background: `
                  radial-gradient(
                    circle at center,
                    rgba(${styles.glowColor}, 0.15) 0%,
                    rgba(${styles.glowColor}, 0.05) 20%,
                    rgba(0,0,0,0) 50%
                  )
                `,
                filter: 'blur(8px)'  // Added slight blur to the glow
              }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Toast;